import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { getDisplayToolMaximumDuration } from '@/tools/display/displayTool.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState } from '@/tools/tool.utilities';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { SelectedSearchItem } from '@/search/search.types';
import type { BaseToolPersistedState, DurationWithUnits, ToolRehydratePayload } from '@/tools/tool.types';
import type { ItemOutputV1 } from '@seeqdev/api/model/ItemOutputV1';

export type DisplayToolAsset = Pick<SelectedSearchItem, 'id' | 'name' | 'type' | 'ancestors' | 'properties'>;

export interface DisplayToolState extends BaseToolPersistedState {
  asset: DisplayToolAsset | undefined;
  isScaled: boolean;
}

export interface DisplayToolActions {
  setAsset: (asset: ItemOutputV1 | SelectedSearchItem | undefined) => void;
  setIsScaled: (isScaled: boolean) => void;
  setSearchName: (name: string) => void;
  setMaximumDuration: (maximumDuration: DurationWithUnits | null | undefined) => void;
  setAdvancedParametersCollapsed: (advancedParametersCollapsed: boolean) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  rehydrate: (dehydratedState: Partial<DisplayToolDehydratedState>) => void;
  reset: () => void;
}

export type DisplayToolDehydratedState = DisplayToolState;

const initialState: DisplayToolState = Object.freeze({
  ...BASE_TOOL_COMMON_PROPS,
  asset: undefined,
  isScaled: true,
});

const displayToolMaximumDurationSelector = createSelector(
  [(state: DisplayToolState) => state.maximumDuration],
  getDisplayToolMaximumDuration,
);

export const dehydrateDisplayToolState = (state: DisplayToolState): DisplayToolDehydratedState => state;

const displayToolActionsFactory: ActionsFactory<DisplayToolState, DisplayToolActions> = (set, _get, resetState) => ({
  setAsset: (asset) =>
    set((draft) => {
      if (!asset) {
        draft.asset = undefined;
        return;
      }

      draft.asset = asset;
    }),

  setIsScaled: (isScaled) =>
    set((draft) => {
      draft.isScaled = isScaled;
    }),

  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setMaximumDuration: (maximumDuration) =>
    set((draft) => {
      draft.maximumDuration = maximumDuration ?? undefined;
    }),

  setAdvancedParametersCollapsed: (advancedParametersCollapsed) =>
    set((draft) => {
      draft.advancedParametersCollapsed = advancedParametersCollapsed;
    }),

  rehydrateForEdit: (payload) => {
    resetState();
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({ payload });

      Object.assign(draft, rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });
    });
  },

  rehydrate: (dehydratedState) =>
    set((draft) => {
      Object.assign(draft, dehydratedState);
    }),

  reset: resetState,
});

const STORE_NAME = 'DisplayTool';

const [DisplayToolStoreProvider, useDisplayToolStoreInstance, useDisplayToolSelector] = createStore(
  STORE_NAME,
  initialState,
  displayToolActionsFactory,
);

export { DisplayToolStoreProvider };

export const useDisplayToolAsset = () => useDisplayToolSelector((state) => state.asset);
export const useDisplayToolIsScaled = () => useDisplayToolSelector((state) => state.isScaled);
export const useDisplayToolName = () => useDisplayToolSelector((state) => state.name);
export const useDisplayToolId = () => useDisplayToolSelector((state) => state.id);
export const useDisplayToolOriginalParameters = () => useDisplayToolSelector((state) => state.originalParameters);
export const useDisplayToolMaximumDuration = () => useDisplayToolSelector(displayToolMaximumDurationSelector);
export const useDisplayToolAdvancedParametersCollapsed = () =>
  useDisplayToolSelector((state) => state.advancedParametersCollapsed);
export const useDisplayToolConfigVersion = () => useDisplayToolSelector((state) => state.configVersion);

export const useDisplayToolActions = () => useDisplayToolStoreInstance().actions;

/**
 * Creates an isolated store instance for tests that need direct state assertions.
 * Prefer provider-based tests unless a direct store instance is required.
 */
export function createTestDisplayToolStore(overrides?: Partial<DisplayToolState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestDisplayToolStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, displayToolActionsFactory);
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreateDisplayToolStore() {
  return createStoreInstance<DisplayToolState, DisplayToolActions>(initialState, displayToolActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistDisplayToolStore = () => {
  const register = useRegisterPersistedStore();
  const store = useDisplayToolStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqDisplayToolStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateDisplayToolState,
    });
  }, [register, store]);
};
